/**
 * Problems Page - Browse all practice problems
 */
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { problemService } from '../services/api';
import { useDebounce } from '../hooks';
import './ProblemsPage.css';

const DIFFICULTIES = ['All', 'Easy', 'Medium', 'Hard'];

function ProblemsPage() {
  const [problems, setProblems] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState('All');
  const [searchParams] = useSearchParams();
  const debouncedSearch = useDebounce(search);

  useEffect(() => {
    const params = {};
    if (searchParams.get('difficulty')) setDifficulty(searchParams.get('difficulty'));
    if (searchParams.get('algorithmId')) params.algorithmId = searchParams.get('algorithmId');

    problemService.getAll(params)
      .then(res => {
        setProblems(res.data || []);
        setFiltered(res.data || []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    let result = [...problems];
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q)
      );
    }
    if (difficulty !== 'All') {
      result = result.filter(p => p.difficulty === difficulty);
    }
    setFiltered(result);
  }, [debouncedSearch, difficulty, problems]);

  const diffColor = { Easy: 'badge-easy', Medium: 'badge-medium', Hard: 'badge-hard' };

  return (
    <div className="problems-page">
      <div className="container">
        <div className="page-header">
          <h1>Practice Problems</h1>
          <p>Apply your algorithm knowledge with hands-on coding challenges</p>
        </div>

        {/* Filters */}
        <div className="filter-bar">
          <div className="search-bar" style={{ flex: 1 }}>
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search problems..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select
            className="filter-select"
            value={difficulty}
            onChange={e => setDifficulty(e.target.value)}
          >
            {DIFFICULTIES.map(d => (
              <option key={d} value={d}>{d === 'All' ? 'All Difficulties' : d}</option>
            ))}
          </select>
        </div>

        {/* Results count */}
        {!loading && (
          <p className="results-info">{filtered.length} problem{filtered.length !== 1 ? 's' : ''}</p>
        )}

        {/* States */}
        {loading && <div className="loader"><div className="spinner" /></div>}
        {error && <div className="error-state card"><p>⚠️ {error}</p></div>}

        {/* Problems List */}
        {!loading && !error && (
          <div className="problems-list">
            {filtered.map((problem, i) => (
              <Link
                key={problem.id}
                to={`/problems/${problem.id}`}
                className="problem-row card card-clickable"
              >
                <div className="problem-num">{i + 1}</div>

                <div className="problem-info">
                  <div className="problem-title-row">
                    <h3 className="problem-title">{problem.title}</h3>
                    <span className={`badge ${diffColor[problem.difficulty]}`}>
                      {problem.difficulty}
                    </span>
                  </div>
                  <p className="problem-desc">{problem.description}</p>
                  <div className="problem-meta">
                    <span className="badge badge-accent">{problem.category}</span>
                    <span className="problem-points">🏆 {problem.points} pts</span>
                    <span className="problem-tests">{problem.testCases?.length || 0} test cases</span>
                  </div>
                </div>

                <div className="problem-arrow">→</div>
              </Link>
            ))}

            {filtered.length === 0 && (
              <div className="empty-state card">
                <p style={{ fontSize: '2rem' }}>🧩</p>
                <p>No problems found. Try adjusting your filters.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProblemsPage;
